import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useState } from "react";
import Modal from "@/components/ui/Modal";
import { TGeneratedScript } from "./ProductStructureType";
import GeneratedScriptDetailsModal from "./GeneratedScriptDetailsModal";

type GenerateScriptsProps = {
  generatedScripts: TGeneratedScript[];
};

const GenerateScripts = ({ generatedScripts }: GenerateScriptsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState<TGeneratedScript | null>(
    null
  );

  if (!generatedScripts || generatedScripts.length === 0) {
    return (
      <div className="bg-[#FDFDFD] border border-[#DDDDDD] py-6 px-[18px] rounded-lg mb-3">
        <p className="text-[#A7A7A7] text-sm text-center">
          No generated scripts available
        </p>
      </div>
    );
  }

  const ScriptCard = ({
    script,
    scriptIndex,
  }: {
    script: TGeneratedScript;
    scriptIndex: number;
  }) => {
    const [scriptDataType, setScriptDataType] = useState<string>("hook");

    const scriptData = [
      {
        id: "hook",
        label: "Hook",
        description: script?.hook || "Hook content not available",
      },
      {
        id: "body",
        label: "Body",
        description: script?.main_content || "Body content not available",
      },
      {
        id: "cta",
        label: "CTA",
        description: script?.call_to_action || "CTA content not available",
      },
    ];

    const currentScriptData =
      scriptData.find((item) => item.id === scriptDataType) || scriptData[0];

    const truncateText = (text: string, maxLength: number = 150) => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    };

    const handleReadMore = () => {
      setSelectedScript(script);
      setIsModalOpen(true);
    };

    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 h-[118px] flex flex-col justify-between">
        {/* Header */}
        <div className="flex items-start justify-between flex-shrink-0">
          <div className="flex-1">
            <h4 className="text-[#0D0D0D] font-semibold text-sm leading-5 mb-1 line-clamp-2">
              {script?.title}
            </h4>
            <div className="flex items-center gap-2 text-xs text-[#667085]">
              <span>{script?.script_type}</span>
              <span>•</span>
              <span>{script?.duration}</span>
            </div>
          </div>
          <div className="text-[#667085] text-xs ml-2">#{scriptIndex + 1}</div>
        </div>
        <button
          onClick={handleReadMore}
          className="text-xs text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors"
        >
          Read more →
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="bg-[#FDFDFD] border border-[#DDDDDD] py-3 px-[18px] rounded-lg mb-3">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[#363636] text-sm leading-6 tracking-normal font-medium">
            Generated Scripts
          </p>
          <div className="text-[#667085] text-xs">
            {generatedScripts.length} script
            {generatedScripts.length > 1 ? "s" : ""}
          </div>
        </div>

        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={16}
          slidesPerView={1}
          navigation={{
            nextEl: ".swiper-button-next-custom",
            prevEl: ".swiper-button-prev-custom",
          }}
          pagination={{
            clickable: true,
            bulletActiveClass: "swiper-pagination-bullet-active-custom",
            bulletClass: "swiper-pagination-bullet-custom",
          }}
          className="generated-scripts-swiper"
        >
          {generatedScripts.map((script, scriptIndex) => (
            <SwiperSlide key={script?.id}>
              <ScriptCard script={script} scriptIndex={scriptIndex} />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Navigation */}
        {generatedScripts.length > 1 && (
          <div className="flex items-center justify-center gap-4 mt-3">
            <button className="swiper-button-prev-custom w-8 h-8 rounded-full border border-[#E5E7EB] flex items-center justify-center hover:bg-[#F9FAFB] transition-colors">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M7.5 9L4.5 6L7.5 3"
                  stroke="#667085"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button className="swiper-button-next-custom w-8 h-8 rounded-full border border-[#E5E7EB] flex items-center justify-center hover:bg-[#F9FAFB] transition-colors">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M4.5 3L7.5 6L4.5 9"
                  stroke="#667085"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={isModalOpen} setOpen={setIsModalOpen} width="900px">
        <GeneratedScriptDetailsModal selectedScript={selectedScript} />
      </Modal>

      {/* Custom Swiper Styles */}
      <style>{`
        .generated-scripts-swiper .swiper-pagination {
          position: relative;
          
        }

        .swiper-pagination-bullet-custom {
          width: 6px;
          height: 6px;
          background: #d1d5db;
          opacity: 1;
          margin: 0 3px;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .swiper-pagination-bullet-active-custom {
          background: #0ea5e9;
          width: 8px;
          height: 8px;
        }

        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default GenerateScripts;
